<template>
  <div
    class="refonte-table-item"
    v-bind:class="`table-item-${props.size} ${props.on_click ? 'table-item-clickable' : ''} ${props.pending ? 'table-item-pending' : ''} ${props.class}`"
    v-bind:style="`${props.width ? `flex: 1 0 ${props.width}px` : ''};${props.min_width && props.sup_text ? `max-width: ${props.min_width}px` : ''}`"
    v-on:click="onItemClick"
    v-tooltip="{
      content: props.tooltip ? props.tooltip : undefined
    }">
    <div
      v-if="!props.no_label"
      class="table-item-label">
      <refonte-pastille
        v-if="props.status"
        v-bind:status="props.status" />
      <div
        v-if="props.label"
        class="table-item-label-text">
        {{ props.label }}
      </div>
      <div
        v-tooltip="{
          content: props.label_icon_tooltip
            ? props.label_icon_tooltip
            : undefined
        }"
        v-if="props.label_icon"
        class="table-item-label-icon">
        <component v-bind:is="props.label_icon" />
      </div>
      <slot
        v-if="!!slots['itemTags']"
        name="itemTags"></slot>
      <slot
        v-if="!!slots['itemDates']"
        name="itemDates"></slot>
    </div>

    <div
      v-if="props.sup_text && typeof props.sup_text === 'string'"
      class="table-item-content-sup-text"
      v-bind:class="props.sup_text_class"
      v-bind:title="props.sup_text">
      {{ props.sup_text }}
    </div>
    <div
      v-else-if="props.sup_text"
      class="table-item-content-sup-text"
      v-bind:class="props.sup_text_class">
      <component v-bind:is="props.sup_text" />
    </div>
    <div class="table-item-content">
      <div
        v-if="props.icon"
        class="table-item-content-icon"
        v-bind:class="props.icon_black ? 'table-item-content-icon-black' : ''">
        <component v-bind:is="props.icon" />
      </div>
      <div class="table-item-content-text">
        <slot name="content"></slot>
      </div>
    </div>
    <div
      v-if="props.tag_text"
      class="table-item-content-sub-text"
      v-bind:class="props.sub_link_class">
      <ul class="m-0">
        <li>
          <refonte-tag
            class="mr-2"
            v-if="props.tag_type_id && props.tag_type_label"
            v-bind:variant="typeMapping[props.tag_type_id]">
            <span class="text-[9px]">{{ props.tag_type_label }}</span>
          </refonte-tag>
          <router-link
            v-if="props.tag_link_path"
            v-bind:to="{ path: props.tag_link_path }"
            class="!text-refonte-black hover:underline"
            title=" "
            v-on:click="
              (e) => {
                e.stopPropagation();
              }
            ">
            {{ props.tag_text }}
          </router-link>
          <span
            v-else
            class="!text-refonte-black">
            {{ props.tag_text }}
          </span>
        </li>
      </ul>
    </div>
    <div
      v-if="props.sub_text && typeof props.sub_text === 'string'"
      class="table-item-content-sub-text"
      v-bind:class="props.sub_text_class">
      {{ props.sub_text }}
    </div>
    <div
      v-else-if="props.sub_text"
      class="table-item-content-sub-text"
      v-bind:class="props.sub_text_class">
      <component v-bind:is="props.sub_text" />
    </div>
    <div
      v-if="props.sub_link_text"
      class="table-item-content-sub-text"
      v-bind:class="props.sub_link_class">
      <span v-if="props.sub_link_prefix">
        {{ props.sub_link_prefix }}
      </span>
      <router-link
        v-if="props.sub_link_path"
        v-bind:to="props.sub_link_path"
        class="!text-refonte-black hover:underline"
        title=" "
        v-on:click="
          (e) => {
            e.stopPropagation();
          }
        ">
        {{ props.sub_link_text }}
      </router-link>
      <span
        v-else
        class="!text-refonte-black">
        {{ props.sub_link_text }}
      </span>
    </div>
  </div>
</template>

<script setup lang="ts">
  import RefontePastille from '@/components/refonte/pastille/refonte-pastille.vue';
  import { useSlots } from 'vue';
  import RefonteTag from '@/components/refonte/tags/refonte-tag.vue';
  import { typeMapping } from '@/types/gestion/inscriptions-types';

  const props = withDefaults(
    defineProps<{
      width?: number;
      min_width?: number;
      size?: 'sm' | 'md';
      pending?: boolean;
      on_click?: Function;
      content_custom_with_on_click?: boolean;
      tooltip?: string;
      no_label?: boolean;
      label?: string;
      label_icon?: Element;
      label_icon_tooltip?: string;
      status?: string;
      icon?: Element;
      icon_black?: boolean;
      sup_text?: string | Element;
      sup_text_class?: string;
      sub_text?: string | Element;
      sub_text_class?: string;
      sub_link_text?: string;
      sub_link_class?: string;
      sub_link_prefix?: string;
      sub_link_path?: string;
      class?: string;
      tag_text?: string;
      tag_link_path?: string;
      tag_variant?: string;
      tag_type_label?: string;
      tag_type_id?: number;
    }>(),
    {
      no_label: false,
      icon_black: false,
      size: 'md',
      class: ''
    }
  );

  const slots = useSlots();

  function onItemClick(e: Event) {
    if (props.on_click) {
      props.on_click();
      e.stopPropagation();
    } else if (props.content_custom_with_on_click) {
      e.stopPropagation();
    }
  }
</script>

<style lang="scss" scoped>
  .refonte-table-item {
    @apply inline-flex flex-col items-start;
    gap: 2px;
    border-radius: 4px;
    max-width: 100%;

    &.table-item-sm {
      padding: 7px 4px;
    }
    &.table-item-md {
      padding: 7px 8px;
    }

    .table-item-label {
      @apply flex flex-wrap items-center justify-start self-stretch overflow-hidden;
      gap: 4px;

      .table-item-label-icon {
        @apply flex shrink-0 items-center justify-center text-refonte_blue-300;
        width: 20px;
        height: 20px;

        :deep(svg) {
          @apply text-refonte_blue-200;
          width: 20px;
          height: 20px;
        }
      }
      .table-item-label-text {
        @apply flex items-center uppercase text-refonte_blue-300;
        padding: 3px 0;
        gap: 2px;
        font-size: 11px;
        font-weight: 400;
        line-height: 14px;
      }
    }
    .table-item-content {
      @apply flex items-start;
      min-height: 20px;
      gap: 4px;

      .table-item-content-icon {
        @apply flex shrink-0 items-center justify-center;
        width: 20px;
        height: 20px;

        :deep(svg) {
          @apply text-refonte_blue-200;
          width: 20px;
          height: 20px;
        }

        &.table-item-content-icon-black {
          :deep(svg) {
            @apply text-refonte-black;
          }
        }
      }
      .table-item-content-text {
        @apply flex-grow text-refonte-black;
        font-size: 14px;
        font-weight: 400;
        line-height: 125%;
      }
    }
    .table-item-content-sub-text {
      @apply text-refonte-black-50;
      font-size: 12px;
      font-weight: 400;
      line-height: 120%;
    }
    .table-item-content-sup-text {
      @apply w-full overflow-hidden text-ellipsis text-refonte-black-50;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      font-size: 12px;
      font-weight: 400;
      line-height: 120%;
    }

    &.table-item-clickable {
      @apply mx-1 cursor-pointer bg-refonte-pale-violet-2;

      &:hover {
        @apply bg-refonte-pale-violet-3;

        .table-item-label {
          @apply text-refonte_blue-600;
        }
      }
    }
    &.table-item-pending {
      .table-item-content {
        .table-item-content-icon {
          &.table-item-content-icon-black {
            :deep(svg) {
              @apply text-refonte_blue-200;
            }
          }
        }
        .table-item-content-text {
          @apply text-refonte_blue-300;
        }
      }
      .table-item-content-sub-text {
        @apply text-refonte_blue-300;
      }
      &.table-item-clickable {
        @apply cursor-default bg-transparent;

        &:hover {
          @apply bg-transparent;

          .table-item-label {
            @apply text-refonte_blue-300;
          }
        }
      }
    }
    &.table-item-with-content-word-break {
      .table-item-content {
        .table-item-content-text {
          word-break: break-all;
        }
      }
    }
  }
</style>
